import { useEffect, useState } from "react";
import { resolveMediaUrl } from "../../helpers/media-url";

/** Fetch authenticated instabot media into a blob URL for <img>/<video>. */
export function AuthenticatedMedia({
  mediaUrl,
  mediaKind,
  alt = "Attachment",
}: {
  mediaUrl?: string | null;
  mediaKind?: string | null;
  alt?: string;
}) {
  const isLocalBlob = Boolean(mediaUrl && mediaUrl.startsWith("blob:"));
  const [remoteBlobUrl, setRemoteBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!mediaUrl || isLocalBlob) return undefined;

    let cancelled = false;
    let objectUrl: string | null = null;
    const href = resolveMediaUrl(mediaUrl);

    fetch(href, {
      credentials: "omit",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`media ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setRemoteBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaUrl, isLocalBlob]);

  const blobUrl = isLocalBlob ? mediaUrl : remoteBlobUrl;

  if (failed) {
    return <div className="ib-bubble__meta">Media unavailable</div>;
  }
  if (!blobUrl) {
    return <div className="ib-bubble__meta">Loading media…</div>;
  }
  if (mediaKind === "video") {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        className="ib-bubble__media"
        src={blobUrl}
        controls
        playsInline
        preload="metadata"
      />
    );
  }
  if (mediaKind === "audio" || mediaKind === "voice") {
    return (
      <div className="ib-voice-note">
        <span className="ib-voice-note__bars" aria-hidden="true">
          <i key="1" />
          <i key="2" />
          <i key="3" />
          <i key="4" />
          <i key="5" />
        </span>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio className="ib-voice-note__audio" src={blobUrl} controls preload="metadata" />
      </div>
    );
  }
  return (
    <a
      href={blobUrl}
      target="_blank"
      rel="noreferrer"
      className="ib-bubble__media-link"
    >
      <img className="ib-bubble__media" src={blobUrl} alt={alt} />
    </a>
  );
}

export function VoiceNoteBubble() {
  return (
    <div className="ib-voice-note ib-voice-note--placeholder">
      <span className="ib-voice-note__bars" aria-hidden="true">
        <i key="1" />
        <i key="2" />
        <i key="3" />
        <i key="4" />
        <i key="5" />
      </span>
      <span className="ib-voice-note__label">Voice note</span>
    </div>
  );
}

export const ChatMedia = AuthenticatedMedia;
